import { Component, Renderer2, ViewEncapsulation, inject, input } from '@angular/core';
import { NgStyle } from '@angular/common';

import { ApInlineSvgDirective } from '@ap/inline-svg';
import { ApThemeService } from '@ap/theme';

import { AvailableIcons, availableIcons } from './icon.model';

/**
 * @deprecated
 * in favor of [`@asn/icon`](https://designsystem.acc.aws.devolksbank.net/index.html?path=/docs/web-components-icon--docs).
 * To be removed with the Angular 23 migration.
 *
 * Icon component for displaying an icon.
 */
@Component({
    selector: 'ap-icon',
    templateUrl: './icon.component.html',
    styleUrls: ['./icon.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    providers: [ApThemeService],
    imports: [NgStyle, ApInlineSvgDirective]
})
export class ApIconComponent {
    private readonly themeService = inject(ApThemeService);
    private readonly renderer = inject(Renderer2);

    /**
     * Required - Name of the icon to show.
     */
    public readonly name = input.required<AvailableIcons | string>();

    /**
     * The size of the icon. Available sizes: extra-small, small, medium, large, extra-large.
     * It is also possible to pass a custom size in the form "<width> [<height>]". E.g.: `50px 100px` which translates to `style="width: 50px; height: 100px"`.
     * When a single value is passed, `height` is set to that same value. E.g.: `50px` translates to `style="width: 50px; height: 50px;`.
     * Only use a custom size if the fixed sizes are too small!
     */
    public readonly size = input('small');

    /**
     * The icon color (the `fill` attribute is used).
     * Available: `secondary`, `inherit`, `success`, `warning`, `error`, `disabled`.
     */
    public readonly color = input('default');

    /**
     * Render a border around the icon. The border will scale in width with the size of your icon.
     * Defaults to false.
     */
    public readonly bordered = input(false);

    /**
     * Render the icon in a circle. Only makes sense when used with a background color or border.
     * Defaults to false.
     */
    public readonly circle = input(false);

    /**
     * Center the icon (using `margin-left: auto; margin-right: auto;`).
     * Defaults to false.
     */
    public readonly centered = input(false);

    /**
     * Add manual size option
     */
    public readonly manualSize = input<number>(undefined);

    /**
     * Add a title to the svg for accessibility. When not set it will add aria-hidden to the svg so it is ignored by screenreaders
     */
    public readonly title = input('');

    private readonly iconSizes = ['extra-small', 'small', 'medium', 'large', 'extra-large'];

    protected onSvgInserted(svg: SVGElement): void {
        this.renderer.setAttribute(svg, 'focusable', 'false');
        this.renderer.setAttribute(svg, 'role', 'img');

        const manualSize = this.manualSize();
        if (manualSize) {
            this.renderer.setStyle(svg, 'width', `${manualSize}px`);
            this.renderer.setStyle(svg, 'height', `${manualSize}px`);
        }
        const title = this.title();
        if (title) {
            const titleId = `svg-title-${Math.round(Math.random() * 10000000)}`;

            svg.setAttribute('aria-labelledby', titleId);

            const titleElm = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            titleElm.setAttribute('id', titleId);
            titleElm.textContent = title;
            svg.appendChild(titleElm);
        } else {
            this.renderer.setAttribute(svg, 'aria-hidden', 'true');
        }
    }

    public getAssetUrl(): string {
        const staticUrl = this.themeService.getStaticUrl();
        const themeFileName = `${this.name()}-${this.themeService.getTheme()}` as AvailableIcons;

        if (availableIcons.indexOf(themeFileName) >= 0) {
            return `${staticUrl}/icon/${themeFileName}.svg`;
        }
        return `${staticUrl}/icon/${this.name()}.svg`;
    }

    public containerClasses(): string[] {
        const classes = ['ap-icon', 'ap-icon-container', `ap-icon--${this.name()}`, `ap-icon--${this.color()}`];

        if (!this.manualSize()) {
            classes.push(this.iconSizeClassName(this.size()));
        } else {
            classes.push('ap-icon--manual');
        }

        if (this.centered()) {
            classes.push('ap-icon--centered');
        }

        if (this.bordered()) {
            classes.push('ap-icon--bordered');
            classes.push(`ap-icon--bordered-${this.color()}`);
        }

        if (this.circle()) {
            classes.push('ap-icon--circle');
        }
        return classes;
    }

    public containerStyle() {
        const size = this.size();

        if (!this.isCustomSize(size)) {
            return {};
        }

        const dimensions = size.match(/\S+/g);
        const width = dimensions[0];
        const height = dimensions[1] || width;

        return {
            width,
            height
        };
    }

    private iconSizeClassName(size: string): string {
        if (this.iconSizes.indexOf(size) >= 0) {
            return `ap-icon--${size}`;
        }

        return 'ap-icon--small';
    }

    /**
     * @todo Put classes on 1 element. There's no need to put some classes on the container and others on this svg element.
     */
    private isCustomSize(size: string): boolean {
        return /\d+/.test(size);
    }
}
